<script setup>
import { computed } from 'vue';
import SmartwelResponsiveSheet from './SmartwelResponsiveSheet.vue';
import SubInfoBody from './SmartwelSubInfoBody.vue';
import { usePaymentsStore } from '@/store/modules/payments';

/**
 * 구독중 사용자에게 노출되는 정기구독 정보 시트.
 * - payments store 의 활성 구독(activeSubscription)을 그대로 표시
 * - 해지 신청 상태(cancelRequestedAt)면 "해지 예정"으로 전환하고 해지→해지취소(resume)로 변경
 */
const emit = defineEmits(['close', 'guide', 'cancel', 'resume']);
// embedded: 시트(오버레이) 대신 탭/페이지에 인라인으로 카드+메뉴만 렌더 (구독관리 '구독 정보' 탭)
defineProps({ embedded: { type: Boolean, default: false } });
const paymentsStore = usePaymentsStore();

const fmtDate = (d) => (d ? String(d).slice(0, 10).replaceAll('-', '.') : '-');

const sub = computed(() => paymentsStore.activeSubscription);
const cancelRequested = computed(() => paymentsStore.isSubscriptionCancelRequested);
const planName = computed(() => sub.value?.subscriptionType?.name || '정기구독');
const startDate = computed(() => fmtDate(sub.value?.accessStartDate));
const endDate = computed(() => fmtDate(sub.value?.accessEndDate));
const nextRenewal = computed(() => fmtDate(sub.value?.nextRenewalAt));
</script>

<template>
  <!-- 오버레이(시트) 형태 — CreditBar/floating-bar 등 기존 동작 -->
  <SmartwelResponsiveSheet v-if="!embedded" @close="emit('close')">
    <div class="popup-header">
      <span class="popup-title">정기구독</span>
    </div>

    <div class="popup-body">
      <SubInfoBody
        :cancel-requested="cancelRequested"
        :plan-name="planName"
        :start-date="startDate"
        :end-date="endDate"
        :next-renewal="nextRenewal"
        @guide="emit('guide')"
        @resume="emit('resume')"
      />
    </div>

    <div class="popup-cta-area">
      <button type="button" class="popup-btn popup-btn-secondary" @click="emit('close')">
        닫기
      </button>
    </div>
  </SmartwelResponsiveSheet>

  <!-- 인라인(탭 내장) 형태 — 구독관리 '구독 정보' 탭 -->
  <div v-else class="popup-body popup-body--embedded">
    <SubInfoBody
      :cancel-requested="cancelRequested"
      :plan-name="planName"
      :start-date="startDate"
      :end-date="endDate"
      :next-renewal="nextRenewal"
      @guide="emit('guide')"
      @resume="emit('resume')"
    />
  </div>
</template>

<style lang="scss" scoped>
.popup-header {
  padding: 16px 24px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
}

.popup-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.popup-body {
  flex: 0 1 auto;
  overflow-y: auto;
  padding: 16px 20px 12px;
}

/* 인라인(탭 내장) — 시트 내부 패딩 제거, 페이지 패딩에 맡김 */
.popup-body--embedded {
  padding: 0;
  overflow: visible;
}

.pop-sub-card {
  background: linear-gradient(135deg, #1e3a5f 0%, #2c4a75 100%);
  border-radius: 14px;
  padding: 18px 20px;
  color: #fff;
  margin-bottom: 14px;
}

.pop-sub-status-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.pop-sub-status {
  font-size: 18px;
  font-weight: 700;
}

.pop-sub-badge {
  background: #10b981;
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 100px;

  &--cancel {
    background: #f59e0b;
  }
}

.pop-sub-cancel-notice {
  background: #fef3e2;
  border-left: 3px solid #f59e0b;
  border-radius: 10px;
  padding: 11px 14px;
  margin-bottom: 14px;
  font-size: 12px;
  line-height: 1.5;
  color: #92400e;
  font-weight: 500;

  strong {
    color: #78350f;
    font-weight: 700;
  }
}

.pop-sub-info-row {
  display: flex;
  justify-content: space-between;
  font-size: 12.5px;
  padding: 3px 0;

  .label {
    opacity: 0.75;
  }

  .value {
    font-weight: 700;
  }
}

.payment-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;

  &.paid {
    background: rgba(16, 185, 129, 0.2);
    color: #6ee7b7;
  }

  &.pending {
    background: rgba(245, 158, 11, 0.2);
    color: #fcd34d;
  }

  &.suspended {
    background: rgba(220, 38, 38, 0.2);
    color: #fca5a5;
  }
}

.pop-menu {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pop-menu-item {
  padding: 12px 4px;
  font-size: 14px;
  color: #1f2937;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  transition: background 0.15s;

  &:hover {
    background: #f9fafb;
  }

  &.danger {
    color: #dc2626;
  }
}

.pop-menu-arrow {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  background: url('@manseImages/icon_dropdown.svg') no-repeat center / contain;
  transform: rotate(-90deg);
}

.popup-cta-area {
  padding: 12px 20px 20px;
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.popup-btn {
  flex: 1;
  height: 48px;
  border-radius: 12px;
  font-size: 14.5px;
  font-weight: 700;
  font-family: inherit;
  letter-spacing: -0.3px;
  cursor: pointer;
  border: none;
  transition: background 0.15s;
}

.popup-btn-secondary {
  background: #f3f4f6;
  color: #4b5563;

  &:hover {
    background: #e5e7eb;
  }
}

/* PC 팝업: body 좌우 32px, CTA 좌우·하단 32px (헤더 제외) */
.srs-sheet--pc .popup-body {
  padding-left: 32px;
  padding-right: 32px;
}
.srs-sheet--pc .popup-cta-area {
  padding-left: 32px;
  padding-right: 32px;
  padding-bottom: 32px;
}
</style>
